import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  fetchTrainingHistoryReportAction,
  setLoading,
} from "../slices/trainingHistoryReportSlice";
import { FetchTrainingHistoryReportParams } from "../api/fetchTrainingHistoryReport/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

export interface TrainingHistoryReportFilters {
  searchTerm?: string;
  locationIds?: number[];
  sessionDateFrom?: string;
  sessionDateTo?: string;
}

function buildFilterSortParams(
  filters: TrainingHistoryReportFilters,
  sorting: SortingState,
): Omit<FetchTrainingHistoryReportParams, "page" | "pageSize"> {
  const params: Omit<FetchTrainingHistoryReportParams, "page" | "pageSize"> =
    {};
  if (filters.searchTerm) params.searchTerm = filters.searchTerm;
  if (filters.locationIds?.length) params.locationIds = filters.locationIds;
  if (filters.sessionDateFrom) params.sessionDateFrom = filters.sessionDateFrom;
  if (filters.sessionDateTo) params.sessionDateTo = filters.sessionDateTo;
  if (sorting.length > 0) {
    params.sortBy = sorting[0].id;
    params.sortOrder = sorting[0].desc ? "DESC" : "ASC";
  }
  return params;
}

export function useTrainingHistoryReport() {
  const dispatch = useAppDispatch();

  const { items, totalCount, loading, error } = useSelector(
    (state: RootState) => state.trainingHistoryReport,
  );

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<TrainingHistoryReportFilters>({});

  useEffect(() => {
    dispatch(
      fetchTrainingHistoryReportAction({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        ...buildFilterSortParams(filters, sorting),
      }),
    );
  }, [dispatch, pagination, sorting, filters]);

  const handlePaginationChange = useCallback(
    (newPagination: Pagination) => {
      dispatch(setLoading(true));
      setPagination(newPagination);
    },
    [dispatch],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      dispatch(setLoading(true));
      setSorting((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (
      key: keyof TrainingHistoryReportFilters,
      value: string | number[] | undefined,
    ) => {
      dispatch(setLoading(true));
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  return {
    items,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    exportParams: buildFilterSortParams(filters, sorting),
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
